import { useEffect, useState } from "react";
import type { ActiveRunSummary, RunDetail } from "../../api/types.js";

type PlaceholderRun = ActiveRunSummary | RunDetail;

function phaseOf(run: PlaceholderRun) {
  return "phase" in run ? run.phase : run.status;
}

function elapsedLabel(createdAt: string, now: number) {
  const started = Date.parse(createdAt);
  if (!Number.isFinite(started)) return "";
  const seconds = Math.max(0, Math.floor((now - started) / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${seconds % 60}s`;
}

export function ActiveRunPlaceholder({
  run,
  onStop,
  stopPending
}: {
  run: PlaceholderRun;
  onStop: () => void;
  stopPending: boolean;
}) {
  const phase = phaseOf(run);
  const active = phase === "running" || phase === "cancelling" || phase === "finalizing";
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  const failedError = "error" in run ? run.error : null;
  const copy = phase === "running"
    ? { heading: "正在继续处理", body: "页面刷新不会中断任务，完成后会自动显示回答" }
    : phase === "cancelling"
      ? { heading: "正在停止", body: "正在保存已生成内容" }
      : phase === "finalizing"
        ? { heading: "回答已经生成", body: "正在保存结果" }
        : { heading: "本次处理未完成", body: failedError || "本次处理没有正常完成" };
  const elapsed = active ? elapsedLabel(run.createdAt, now) : "";
  return (
    <div className="active-run-placeholder" data-active-run-placeholder tabIndex={-1}>
      {/* 计时文本放在 live region 之外，避免每秒都被读屏播报。 */}
      <div className="active-run-placeholder-copy" role="status" aria-live="polite">
        <strong>{copy.heading}</strong>
        <p>{copy.body}</p>
      </div>
      {elapsed && <p className="active-run-elapsed">已处理 {elapsed}</p>}
      {phase === "running" && !stopPending && (
        <button type="button" className="active-run-stop" onClick={onStop}>停止</button>
      )}
    </div>
  );
}
